import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Block } from "@mui/icons-material";

import TruncatedWithTooltip from "../../../TruncatedWithTooltip";
import {
  UICard,
  UIPaginationBar,
  UITable,
} from "../../../ui";
import { TIMESHEET_EMPTY_STATE_VARIANTS } from "./constants";
import EventTimesheetToolbar from "./EventTimesheetToolbar";
import TimesheetApplicantStatusBadge from "./TimesheetApplicantStatusBadge";
import TimesheetEmptyState from "./TimesheetEmptyState";
import { getTimesheetApplicantName, normalizeTimesheetApplicantId } from "./timesheetUtils";

import { formatWorkingHoursDuration } from "../../../../utils/Helpers";

import "./EventTimesheet.scss";

const formatDayCount = (value) =>
  value != null && value !== "" ? String(value) : "0";

const EventTimesheetListPanel = ({
  copy,
  applicantsLabels,
  lang,
  rows,
  isLoading,
  isEventStarted,
  search,
  filters,
  roleOptions,
  statusOptions,
  page,
  pageSize,
  total,
  pages,
  rangeFrom,
  rangeTo,
  onSearchChange,
  onFilterChange,
  onExportAll,
  onRowClick,
  onPageChange,
  onPageSizeChange,
  exportDisabled,
  showExportAll = true,
  hasActiveSearchOrFilter,
}) => {
  const tableRows = useMemo(
    () =>
      (rows || []).map((row, index) => {
        const id = normalizeTimesheetApplicantId(
          row.id ?? row.working_log_id ?? row.user?.id ?? `row-${index}`,
        );

        return {
          ...row,
          id,
        };
      }),
    [rows],
  );

  const columns = useMemo(
    () => [
      {
        field: "applicantName",
        headerName: copy.applicantName,
        sortable: false,
        valueGetter: (params) => getTimesheetApplicantName(params.row, lang),
        renderCell: (params) => {
          const value = params.value || "-";
          if (value === "-") {
            return <span className="MuiDataGrid-cellContent">-</span>;
          }

          return (
            <>
              <TruncatedWithTooltip title={value}>
                <span className="MuiDataGrid-cellContent">{value}</span>
              </TruncatedWithTooltip>
              {params.row.user?.is_blocked ? (
                <Block color="error" fontSize="small" sx={{ flexShrink: 0 }} />
              ) : null}
            </>
          );
        },
      },
      {
        field: "working_days",
        headerName: copy.workingDays,
        sortable: false,
        valueFormatter: ({ value }) => formatDayCount(value),
      },
      {
        field: "working_hours",
        headerName: copy.workingHours,
        sortable: false,
        valueFormatter: ({ value }) => formatWorkingHoursDuration(value, lang),
      },
      {
        field: "training_days",
        headerName: copy.trainingDays,
        sortable: false,
        valueFormatter: ({ value }) => formatDayCount(value),
      },
      {
        field: "status",
        headerName: copy.status,
        sortable: false,
        renderCell: (params) => (
          <TimesheetApplicantStatusBadge
            status={params.value}
            label={copy.statuses[params.value] || params.value}
          />
        ),
      },
    ],
    [copy, lang],
  );

  const emptyVariant = !isEventStarted
    ? TIMESHEET_EMPTY_STATE_VARIANTS.event
    : hasActiveSearchOrFilter
      ? TIMESHEET_EMPTY_STATE_VARIANTS.filtered
      : TIMESHEET_EMPTY_STATE_VARIANTS.event;

  const showEmptyState = !isLoading && !tableRows.length;

  return (
    <div className="event-timesheet">
      <EventTimesheetToolbar
        copy={copy}
        search={search}
        onSearchChange={onSearchChange}
        onExportAll={onExportAll}
        exportDisabled={exportDisabled}
        showExportAll={showExportAll}
        filters={filters}
        roleOptions={roleOptions}
        statusOptions={statusOptions}
        onFilterChange={onFilterChange}
      />

      {showEmptyState ? (
        <UICard className="event-timesheet__empty-card">
          <TimesheetEmptyState variant={emptyVariant} copy={copy} />
        </UICard>
      ) : (
        <div className="event-timesheet__table-shell">
          <UITable
            rows={tableRows}
            columns={columns}
            pageSize={pageSize}
            contentSized
            hideFooter
            getRowId={(row) => row.id}
            onRowClick={onRowClick}
            getRowClassName={() => "event-timesheet__row"}
          />
          {total > 0 ? (
            <UIPaginationBar
              currentPage={page}
              pages={pages}
              pageSize={pageSize}
              rangeTemplate={applicantsLabels.rangeTemplate}
              rangeFrom={rangeFrom}
              rangeTo={rangeTo}
              rangeTotal={total}
              rowsPerPageLabel={applicantsLabels.rowsPerPageLabel}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
              className="event-timesheet__pagination"
            />
          ) : null}
        </div>
      )}
    </div>
  );
};

EventTimesheetListPanel.propTypes = {
  copy: PropTypes.object.isRequired,
  applicantsLabels: PropTypes.shape({
    rangeTemplate: PropTypes.string.isRequired,
    rowsPerPageLabel: PropTypes.string.isRequired,
  }).isRequired,
  lang: PropTypes.string.isRequired,
  rows: PropTypes.array,
  isLoading: PropTypes.bool,
  isEventStarted: PropTypes.bool,
  search: PropTypes.string,
  filters: PropTypes.object,
  roleOptions: PropTypes.array,
  statusOptions: PropTypes.array,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  total: PropTypes.number,
  pages: PropTypes.number,
  rangeFrom: PropTypes.number,
  rangeTo: PropTypes.number,
  onSearchChange: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onExportAll: PropTypes.func,
  onRowClick: PropTypes.func,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  exportDisabled: PropTypes.bool,
  showExportAll: PropTypes.bool,
  hasActiveSearchOrFilter: PropTypes.bool,
};

EventTimesheetListPanel.defaultProps = {
  rows: [],
  isLoading: false,
  isEventStarted: true,
  search: "",
  filters: {},
  roleOptions: [],
  statusOptions: [],
  page: 1,
  pageSize: 10,
  total: 0,
  pages: 1,
  rangeFrom: 0,
  rangeTo: 0,
  onExportAll: undefined,
  onRowClick: undefined,
  exportDisabled: true,
  showExportAll: true,
  hasActiveSearchOrFilter: false,
};

export default EventTimesheetListPanel;
